"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react"

import {
  useAgentCoreClient,
  DEFAULT_AGENT_NAME,
  type AgentName,
} from "@/lib/agentcore-client"
import { useAuth } from "@/hooks/useAuth"
import {
  createSession,
  updateSession,
  getSessionMessages,
  checkSessionExists,
} from "@/services/sessionsService"
import type { Message, MessageSegment, ToolCall } from "./types"
import { useInvocationContext } from "./InvocationContext"

/**
 * Mutable per-session state held by the store. One of these exists for every
 * session the user has interacted with during the current page lifetime,
 * regardless of which session is currently on screen. This is what allows a
 * background stream to keep writing into its own session after the user
 * switches away, and to be shown again when they switch back.
 */
interface SessionState {
  /** All messages (user + assistant) for this session, in order. */
  messages: Message[]
  /** Error banner text for this session, or null when there is none. */
  error: string | null
  /** True while the initial history fetch for this session is in flight. */
  loadingHistory: boolean
  /**
   * True while an agent response is actively streaming into this session.
   * Drives the input's disabled/"Thinking..." state for the active session.
   */
  isStreaming: boolean
  /**
   * True once this session has been persisted to DynamoDB.
   */
  persisted: boolean
  /**
   * True once history has been loaded (or determined to be empty) for this
   * session. Used to avoid re-fetching history when the user returns to a
   * session that already has in-memory messages from a live/finished stream.
   */
  historyLoaded: boolean
  /** AbortController for the in-flight request, if any. */
  abort: AbortController | null
  /** Draft input text for this session, preserved across session switches. */
  input: string
  /**
   * Monotonic timestamp (ms) of the last time this slice was read or written.
   * Drives least-recently-used eviction so idle, finished, off-screen sessions
   * are dropped first once the retained-session cap is exceeded.
   */
  lastAccessed: number
}

/**
 * Maximum number of session slices retained in memory at once. Beyond this,
 * the least-recently-accessed sessions that are safe to drop (not on screen
 * and not currently streaming) are evicted. Evicted sessions transparently
 * reload their history from the backend when the user returns to them.
 */
const MAX_RETAINED_SESSIONS = 5

/** Snapshot of a session's state exposed to the view layer (read-only). */
export interface SessionView {
  messages: Message[]
  error: string | null
  loadingHistory: boolean
  isStreaming: boolean
  input: string
}

/** Public API of the chat sessions store. */
interface ChatSessionsContextValue {
  /**
   * Read the current view snapshot for a session. Returns a stable default for
   * sessions that have never been touched (fresh/new chat).
   *
   * @param sessionId - The session to read.
   * @returns The session's view snapshot.
   */
  getSession: (sessionId: string) => SessionView
  /**
   * Send a user message in a session and stream the agent response into that
   * same session's slice. Safe to call while other sessions are streaming.
   *
   * @param sessionId - The session the message belongs to.
   * @param userMessage - The user's prompt text.
   * @param extraBody - Optional agent-specific, request-scoped fields to merge
   *   into the runtime invocation body. Forwarded opaquely to the client; the
   *   store does not interpret the keys.
   */
  sendMessage: (
    sessionId: string,
    userMessage: string,
    extraBody?: Record<string, unknown>
  ) => Promise<void>
  /**
   * Load conversation history for an existing session from the backend, unless
   * it already has in-memory messages (e.g. from a stream that ran while the
   * session was off screen), in which case the in-memory state is kept.
   *
   * @param sessionId - The session to load.
   */
  loadHistory: (sessionId: string) => Promise<void>
  /**
   * Reset a session's slice to empty (used when starting a new chat that reuses
   * an id, or when a load fails). Aborts any in-flight request for that session.
   *
   * @param sessionId - The session to reset.
   */
  resetSession: (sessionId: string) => void
  /**
   * Update the draft input text for a session.
   *
   * @param sessionId - The session whose draft to update.
   * @param input - The new draft text.
   */
  setInput: (sessionId: string, input: string) => void
  /**
   * Set (or clear) the error banner text for a session.
   *
   * @param sessionId - The session to update.
   * @param error - The error text, or null to clear.
   */
  setError: (sessionId: string, error: string | null) => void
}

/** Inert default so consumers do not crash without a provider. */
const DEFAULT_VIEW: SessionView = {
  messages: [],
  error: null,
  loadingHistory: false,
  isStreaming: false,
  input: "",
}

const ChatSessionsCtx = createContext<ChatSessionsContextValue>({
  getSession: () => DEFAULT_VIEW,
  sendMessage: async () => {},
  loadHistory: async () => {},
  resetSession: () => {},
  setInput: () => {},
  setError: () => {},
})

/**
 * Hook to access the chat sessions store.
 *
 * @returns The chat sessions context value.
 */
export function useChatSessions(): ChatSessionsContextValue {
  return useContext(ChatSessionsCtx)
}

/** Props for {@link ChatSessionsProvider}. */
interface ChatSessionsProviderProps {
  /**
   * Called after a session is successfully persisted (created or updated) so
   * the parent can refresh the sidebar.
   */
  onSessionPersisted?: () => void
  /**
   * Called when loading an existing session's history fails or the session no
   * longer exists. The parent should reset to a fresh session.
   *
   * @param sessionId - The session that failed to load.
   */
  onSessionLoadFailed?: (sessionId: string) => void
  /** Called to request a sidebar refresh (e.g. session no longer exists). */
  onRefreshSidebar?: () => void
  /** Canonical agent id used when creating sessions. */
  agent?: AgentName
  /**
   * The session currently on screen.
   */
  activeSessionId: string
}

/**
 * Provider that owns per-session chat state and the streaming lifecycle.
 *
 * State lives in a ref-held Map so that a stream can keep mutating a session's
 * slice even when the component tree is showing a different session. A `version`
 * counter is bumped on every mutation to trigger re-renders; the view layer
 * reads the slice for whichever session it is displaying.
 *
 * @param onSessionPersisted - Sidebar refresh after persistence.
 * @param onSessionLoadFailed - Reset handler when a load fails.
 * @param onRefreshSidebar - Sidebar refresh request.
 * @param agent - Agent id used when creating sessions.
 * @param activeSessionId - The on-screen session, protected from eviction.
 * @param children - The chat page subtree.
 */
export function ChatSessionsProvider({
  onSessionPersisted,
  onSessionLoadFailed,
  onRefreshSidebar,
  agent = DEFAULT_AGENT_NAME,
  activeSessionId,
  children,
}: PropsWithChildren<ChatSessionsProviderProps>): React.JSX.Element {
  const client = useAgentCoreClient()
  const auth = useAuth()

  // Generic "session persisted" seam. Fired once per session, from inside the persistence block, so
  // a listener's write cannot beat the createSession write on a first message
  // and later messages don't trigger redundant writes.
  const { notifyPersisted } = useInvocationContext()

  // The single source of truth. Held in a ref so background stream callbacks
  // mutate the live object rather than a stale closure snapshot.
  const sessionsRef = useRef<Map<string, SessionState>>(new Map())

  // Latest active session id, mirrored into a ref so the eviction pass (called
  // from stable callbacks) always sees the current value without being a
  // dependency of those callbacks.
  const activeSessionIdRef = useRef<string>(activeSessionId)
  activeSessionIdRef.current = activeSessionId

  // Bumped on every mutation to force a re-render of subscribed views. The
  // value is threaded into the memoized context value's dependency list so a
  // mutation produces a new context value identity, re-rendering consumers.
  const [version, setVersion] = useState(0)
  const notify = useCallback((): void => setVersion(v => v + 1), [])

  /**
   * Evict the least-recently-accessed session slices once the map exceeds
   * {@link MAX_RETAINED_SESSIONS}. A slice is only ever dropped when it is both
   * off screen (not the active session) and not currently streaming, so a
   * background response is never discarded mid-flight and the visible session
   * is always retained. Dropped sessions reload from the backend on return.
   */
  const evict = useCallback((): void => {
    const map = sessionsRef.current
    if (map.size <= MAX_RETAINED_SESSIONS) return

    // Candidates for removal: everything except the active + streaming sessions.
    const activeId = activeSessionIdRef.current
    const candidates = [...map.entries()].filter(
      ([id, state]) => id !== activeId && !state.isStreaming
    )

    // Number we must drop to get back to the cap. Protected slices still count
    // toward size, so it is possible (with many concurrent streams) to stay
    // above the cap — that is intentional; we never drop protected sessions.
    let toRemove = map.size - MAX_RETAINED_SESSIONS
    if (toRemove <= 0 || candidates.length === 0) return

    // Oldest first, so we evict the least-recently-used candidates.
    candidates.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed)
    for (const [id] of candidates) {
      if (toRemove <= 0) break
      map.delete(id)
      toRemove -= 1
    }
  }, [])

  /**
   * Get the mutable state for a session, creating an empty slice on first use
   * (and running an eviction pass when a new slice is added). Touches the
   * slice's recency stamp so recently-used sessions survive eviction.
   *
   * @param sessionId - The session to fetch or create.
   * @returns The mutable session state.
   */
  const ensure = useCallback(
    (sessionId: string): SessionState => {
      let state = sessionsRef.current.get(sessionId)
      if (!state) {
        state = {
          messages: [],
          error: null,
          loadingHistory: false,
          isStreaming: false,
          persisted: false,
          historyLoaded: false,
          abort: null,
          input: "",
          lastAccessed: Date.now(),
        }
        sessionsRef.current.set(sessionId, state)
        // A new slice may push us over the cap; prune stale ones now.
        evict()
      } else {
        state.lastAccessed = Date.now()
      }
      return state
    },
    [evict]
  )

  const getSession = useCallback(
    (sessionId: string): SessionView => {
      const state = sessionsRef.current.get(sessionId)
      if (!state) return DEFAULT_VIEW
      return {
        messages: state.messages,
        error: state.error,
        loadingHistory: state.loadingHistory,
        isStreaming: state.isStreaming,
        input: state.input,
      }
    },
    // sessionsRef is stable; re-renders are driven by `version` via notify().
    []
  )

  const setInput = useCallback(
    (sessionId: string, input: string): void => {
      ensure(sessionId).input = input
      notify()
    },
    [ensure, notify]
  )

  const setError = useCallback(
    (sessionId: string, error: string | null): void => {
      ensure(sessionId).error = error
      notify()
    },
    [ensure, notify]
  )

  const resetSession = useCallback(
    (sessionId: string): void => {
      const existing = sessionsRef.current.get(sessionId)
      existing?.abort?.abort()
      sessionsRef.current.set(sessionId, {
        messages: [],
        error: null,
        loadingHistory: false,
        isStreaming: false,
        persisted: false,
        historyLoaded: false,
        abort: null,
        input: "",
        lastAccessed: Date.now(),
      })
      notify()
    },
    [notify]
  )

  const loadHistory = useCallback(
    async (sessionId: string): Promise<void> => {
      const state = ensure(sessionId)

      // If this session already has in-memory messages (from a stream that ran
      // while it was off screen) or history was already loaded, do not refetch —
      // that would drop the just-streamed, not-yet-persisted assistant message.
      if (state.messages.length > 0 || state.historyLoaded) return

      if (!client || client.isLocalMode()) {
        state.historyLoaded = true
        return
      }

      state.loadingHistory = true
      state.error = null
      notify()

      try {
        const token = await auth.getAccessToken()
        if (!token) return

        // Verify the session still exists (could have been deleted elsewhere).
        const exists = await checkSessionExists(sessionId, token)
        if (!exists) {
          state.error = "This session no longer exists"
          state.messages = []
          notify()
          onRefreshSidebar?.()
          onSessionLoadFailed?.(sessionId)
          return
        }

        const history = await getSessionMessages(sessionId, token)

        // Map the API response to the local Message type.
        const loadedMessages: Message[] = history.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          segments: msg.segments?.map(seg => {
            if (seg.type === "text") {
              return { type: "text" as const, content: seg.content || "" }
            }
            return {
              type: "tool" as const,
              toolCall: {
                toolUseId: seg.toolCall?.toolUseId || "",
                name: seg.toolCall?.name || "",
                input: seg.toolCall?.input || "",
                status: (seg.toolCall?.status || "complete") as "complete",
                result: seg.toolCall?.result,
              },
            }
          }),
        }))

        // A stream may have started for this session while history was loading.
        // Only apply the fetched history if the slice is still empty.
        if (state.messages.length === 0) {
          state.messages = loadedMessages
        }
        state.persisted = true
        state.historyLoaded = true
        notify()
      } catch (err) {
        console.error("Failed to load session history:", err)
        state.error = "Failed to load conversation history"
        state.messages = []
        notify()
        onSessionLoadFailed?.(sessionId)
      } finally {
        state.loadingHistory = false
        notify()
      }
    },
    [client, auth, ensure, notify, onRefreshSidebar, onSessionLoadFailed]
  )

  const sendMessage = useCallback(
    async (
      sessionId: string,
      userMessage: string,
      extraBody?: Record<string, unknown>
    ): Promise<void> => {
      if (!userMessage.trim() || !client) return

      const state = ensure(sessionId)

      // Abort any prior in-flight request for THIS session only. Other sessions'
      // streams are untouched and keep running in the background.
      state.abort?.abort()
      const abortController = new AbortController()
      state.abort = abortController

      state.error = null

      // Append the user message and a streaming assistant placeholder.
      const newUserMessage: Message = {
        role: "user",
        content: userMessage,
        timestamp: new Date().toISOString(),
      }
      const assistantResponse: Message = {
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
        isStreaming: true,
      }
      state.messages = [...state.messages, newUserMessage, assistantResponse]
      state.isStreaming = true
      state.input = ""
      notify()

      // Local accumulators for the streaming assistant message.
      const segments: MessageSegment[] = []
      const toolCallMap = new Map<string, ToolCall>()

      /**
       * Recompute the trailing assistant message from the accumulators and
       * publish it into this session's slice. Writes always target `sessionId`
       * regardless of what is on screen.
       */
      const updateMessage = (): void => {
        const content = segments
          .filter((s): s is Extract<MessageSegment, { type: "text" }> => s.type === "text")
          .map(s => s.content)
          .join("")

        const updated = [...state.messages]
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content,
          segments: [...segments],
        }
        state.messages = updated
        notify()
      }

      try {
        const accessToken = await auth.getAccessToken()
        if (!accessToken) {
          throw new Error("Authentication required. Please log in again.")
        }

        // Persist the session row before invoking so server-side token usage
        // has a row to write to. Scoped to this session's persisted flag, so
        // this block runs only on the first message of a session.
        if (client && !client.isLocalMode() && !state.persisted) {
          try {
            await createSession(
              { sessionId, firstMessage: userMessage, agent },
              accessToken
            )
            state.persisted = true
            onSessionPersisted?.()
          } catch (sessionErr) {
            // 409 => already exists (e.g. after a refresh). Treat as persisted.
            if (sessionErr instanceof Error && sessionErr.message.includes("409")) {
              state.persisted = true
            } else {
              console.error("Failed to persist session metadata:", sessionErr)
            }
          }

          // Notify persist listeners exactly once — here, on the transition to
          // persisted
          if (state.persisted) {
            notifyPersisted()
          }
        }

        // User identity is derived server-side from the JWT, never the payload.
        await client.invoke(
          userMessage,
          sessionId,
          accessToken,
          event => {
            switch (event.type) {
              case "text": {
                const prev = segments[segments.length - 1]
                if (prev && prev.type === "tool") {
                  for (const tc of toolCallMap.values()) {
                    if (tc.status === "streaming" || tc.status === "executing") {
                      tc.status = "complete"
                    }
                  }
                }
                const last = segments[segments.length - 1]
                if (last && last.type === "text") {
                  last.content += event.content
                } else {
                  segments.push({ type: "text", content: event.content })
                }
                updateMessage()
                break
              }
              case "tool_use_start": {
                const tc: ToolCall = {
                  toolUseId: event.toolUseId,
                  name: event.name,
                  input: "",
                  status: "streaming",
                }
                toolCallMap.set(event.toolUseId, tc)
                segments.push({ type: "tool", toolCall: tc })
                updateMessage()
                break
              }
              case "tool_use_delta": {
                const tc = toolCallMap.get(event.toolUseId)
                if (tc) tc.input += event.input
                updateMessage()
                break
              }
              case "tool_result": {
                const tc = toolCallMap.get(event.toolUseId)
                if (tc) {
                  tc.result = event.result
                  tc.status = "complete"
                }
                updateMessage()
                break
              }
              case "message": {
                if (event.role === "assistant") {
                  for (const tc of toolCallMap.values()) {
                    if (tc.status === "streaming") tc.status = "executing"
                  }
                  updateMessage()
                }
                break
              }
            }
          },
          abortController.signal,
          extraBody
        )

        // If the request was aborted (session deleted, or superseded by a newer
        // send) while the stream was finishing, stop here: the slice may have
        // been reset and the backend row may be gone, so neither the completion
        // write nor the updateSession call should run.
        if (abortController.signal.aborted) return

        // Mark the trailing assistant message complete.
        const finalized = [...state.messages]
        finalized[finalized.length - 1] = {
          ...finalized[finalized.length - 1],
          isStreaming: false,
        }
        state.messages = finalized
        notify()

        // Bump lastModifiedAt for existing sessions.
        if (client && !client.isLocalMode() && state.persisted) {
          try {
            await updateSession(sessionId, accessToken)
            onSessionPersisted?.()
          } catch (updateErr) {
            // A 409 here means the session row is gone. If we were aborted in the
            // meantime (e.g. the user deleted this session), the deletion is
            // intentional — swallow it silently rather than surfacing an error.
            if (updateErr instanceof Error && updateErr.message.includes("409")) {
              if (abortController.signal.aborted) return
              state.error = "This session no longer exists"
              notify()
              onRefreshSidebar?.()
              onSessionLoadFailed?.(sessionId)
              return
            }
            console.error("Failed to update session:", updateErr)
          }
        }
      } catch (err) {
        // Intentional cancellation (e.g. a newer send superseded this one).
        if (err instanceof DOMException && err.name === "AbortError") {
          return
        }
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        state.error = `Failed to get response: ${errorMessage}`
        console.error("Error invoking AgentCore:", err)

        const errored = [...state.messages]
        errored[errored.length - 1] = {
          ...errored[errored.length - 1],
          content:
            "I apologize, but I encountered an error processing your request. Please try again.",
          segments: undefined,
          isStreaming: false,
        }
        state.messages = errored
        notify()
      } finally {
        // Only clear streaming/abort if this request is still the current one;
        // a newer send for the same session may have replaced them.
        if (state.abort === abortController) {
          state.abort = null
          state.isStreaming = false
        }
        notify()
      }
    },
    [client, auth, agent, ensure, notify, notifyPersisted, onSessionPersisted, onRefreshSidebar, onSessionLoadFailed]
  )

  // `version` is intentionally in the dependency list: bumping it on each
  // mutation yields a new context value identity so subscribed views re-render
  // with fresh data read from the ref-held session map.
  const value = useMemo<ChatSessionsContextValue>(
    () => ({ getSession, sendMessage, loadHistory, resetSession, setInput, setError }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSession, sendMessage, loadHistory, resetSession, setInput, setError, version]
  )

  return <ChatSessionsCtx.Provider value={value}>{children}</ChatSessionsCtx.Provider>
}
